import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import type {
  MemberCount,
  AudienceList,
  AudienceListApiResponse,
  AudienceListSource,
} from 'src/types/audience-list';
import type {JourneyAudienceState} from 'src/reducers/journey-audience';
import chunk from 'lodash/chunk';
import {
  getTrackingProps,
  normalizeAudienceBlocks,
} from 'src/utils/audience-list';

import {thunkify as flow} from 'src/utils/thunks';
import {key, fetching} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api-bff';
import {AnalyticsService} from 'src/analytics';
import {ENGAGE} from 'src/analytics/constants_event';


export type AudienceFormSteps =
  | 'createMode'
  | 'details'
  | 'copyLists'
  | 'rules'
  | 'review'
  | 'pickNLink'
  | 'summary';
export type UpdateStatePayload = Partial<JourneyAudienceState>;

const MAX_BATCH_LENGTH = 3;
export const UPDATE_STATE = 'journey-audience/update';
export const RECEIVE_SELECTED_LISTS = 'journey-audience/receive-selected-lists';
export const SET_INCLUDED_LISTS = 'journey-audience/set-included-lists';
export const SET_EXCLUDED_LISTS = 'journey-audience/set-excluded-lists';
export const RESET = 'journey-audience/reset';
export const GOTO_STEP = 'journey-audience/goto-step';
export const SET_MEMBER_COUNT = 'journey-audience/set-member-count';
export const SET_MEMBER_COUNT_LOADING =
  'journey-audience/set-member-count-loading';

type UpdateStateAction = {
  type: 'journey-audience/update';
  payload: UpdateStatePayload;
};
type ReceiveSelectedListsAction = {
  type: 'journey-audience/receive-selected-lists';
  payload: {
    includedLists: Map<string, Partial<AudienceList>>;
    excludedLists: Map<string, Partial<AudienceList>>;
  };
};
type SetIncludedListsAction = {
  type: 'journey-audience/set-included-lists';
  payload: Map<string, Partial<AudienceList>>;
};
type SetExcludedListsAction = {
  type: 'journey-audience/set-excluded-lists';
  payload: Map<string, Partial<AudienceList>>;
};
type ResetStateAction = {
  type: 'journey-audience/reset';
};
type SetMemberCountAction = {
  type: 'journey-audience/set-member-count';
  payload: Map<string, MemberCount>;
};
type SetMemberCountLoadingAction = {
  type: 'journey-audience/set-member-count-loading';
  payload: {
    listIds: Array<string>;
    isLoading: boolean;
  };
};

export type JourneyAudienceAction =
  | UpdateStateAction
  | ReceiveSelectedListsAction
  | SetIncludedListsAction
  | SetExcludedListsAction
  | SetMemberCountAction
  | SetMemberCountLoadingAction
  | ResetStateAction;

export const updateState = (
  payload: UpdateStatePayload,
): UpdateStateAction => ({
  type: UPDATE_STATE,
  payload,
});

export const receiveSelectedLists = (payload: {
  includedLists: Map<string, Partial<AudienceList>>;
  excludedLists: Map<string, Partial<AudienceList>>;
}): ReceiveSelectedListsAction => ({
  type: RECEIVE_SELECTED_LISTS,
  payload,
});
export const setIncludedLists = (
  payload: Map<string, Partial<AudienceList>>,
): SetIncludedListsAction => ({
  type: SET_INCLUDED_LISTS,
  payload,
});
export const setExcludedLists = (
  payload: Map<string, Partial<AudienceList>>,
): SetExcludedListsAction => ({
  type: SET_EXCLUDED_LISTS,
  payload,
});
export const setMemberCount = (
  payload: Map<string, MemberCount>,
): SetMemberCountAction => ({
  type: SET_MEMBER_COUNT,
  payload,
});
export const setMemberCountLoading = (payload: {
  listIds: Array<string>;
  isLoading: boolean;
}): SetMemberCountLoadingAction => ({
  type: SET_MEMBER_COUNT_LOADING,
  payload,
});
export const reset = (): ResetStateAction => ({type: RESET});

// adding isDbCleanup as second argument as we need to handle a dbCleanup workflows differently on backend.
export const linkAudienceListsToJourney: (
  journeyId: string,
  isDbCleanup?: boolean,
) => ThunkAction<unknown> =
  (journeyId, isDbCleanup = false) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const includedListIds = [
      ...getState().journeyAudience.includedLists.keys(),
    ];
    const excludedListIds = [
      ...getState().journeyAudience.excludedLists.keys(),
    ];
    await dispatch(
      reduxApi.post(
        `workflows/map`,
        {
          includedIds: includedListIds,
          excludedIds: excludedListIds,
          workflowId: journeyId,
          dbCleanup: isDbCleanup,
        },
        undefined,
        {apiPath: '/bff/api/v1/'},
      ),
    ).then(() => {
      AnalyticsService.track(ENGAGE.JOURNEY_LIST_ADDED, {
        workflow_id: journeyId,
        list_addition_method: 'existing_list',
        included_list_ids: includedListIds,
        excluded_list_ids: excludedListIds,
      });
    });
    return {includedListIds, excludedListIds};
  };
export const updateSelectedLists: (listDetails: {
  includedLists: Map<string, Partial<AudienceList>>;
  excludedLists: Map<string, Partial<AudienceList>>;
}) => ReceiveSelectedListsAction = (listDetails) =>
  receiveSelectedLists(listDetails);

export const getJourneyAudience: (
  journeyId: string,
  isDbCleanup?: boolean,
) => ThunkAction<unknown> = flow(
  key((journeyId) => `journeyAudience/get-${journeyId}`),
  fetching(),
)((journeyId, isDbCleanup = false) => async (dispatch: Dispatch) => {
  const payload = await dispatch(
    reduxApi.get(`workflows/${journeyId}/mappings`, {dbCleanup: isDbCleanup}),
  );
  const includedLists = new Map(
    (payload ?? [])
      // @ts-expect-error - TS7006 - Parameter 'resource' implicitly has an 'any' type.
      .filter((resource) => resource.mappingType === 'include')
      // @ts-expect-error - TS7006 - Parameter 'resource' implicitly has an 'any' type.
      .map((resource) => [resource.filter.id, resource.filter]),
  );

  const excludedLists = new Map(
    (payload ?? [])
      // @ts-expect-error - TS7006 - Parameter 'resource' implicitly has an 'any' type.
      .filter((resource) => resource.mappingType === 'exclude')
      // @ts-expect-error - TS7006 - Parameter 'resource' implicitly has an 'any' type.
      .map((resource) => [resource.filter.id, resource.filter]),
  );
  // @ts-expect-error - TS2322 - Type 'Map<unknown, unknown>' is not assignable to type 'Map<string, Partial<AudienceList>>'. | TS2322 - Type 'Map<unknown, unknown>' is not assignable to type 'Map<string, Partial<AudienceList>>'.
  dispatch(receiveSelectedLists({includedLists, excludedLists}));
  return payload;
});

export const getBulkMembersCount: (
  listIds: Array<string>,
) => ThunkAction<unknown> = flow(
  key((listIds) => `journeyAudience/${listIds.join(',')}`),
  fetching(),
)((listIds) => async (dispatch: Dispatch) => {
  dispatch(setMemberCountLoading({listIds, isLoading: true}));
  const listBatches = chunk(listIds, MAX_BATCH_LENGTH);
  const executeBatchCall = async () => {
    for (const listBatch of listBatches) {
      try {
        const response = await dispatch(
          reduxApi.post('filter/bulk-filter-member-count', {
            filterIds: listBatch,
          }),
        );
        const {mappings = {}} = response ?? {};
        const newMappings = new Map<string, MemberCount>([]);
        Object.keys(mappings).forEach((key) => {
          newMappings.set(key, mappings[key]);
        });
        await dispatch(setMemberCount(newMappings));
      } catch (e: any) {
        await dispatch(
          setMemberCountLoading({listIds: listBatch, isLoading: false}),
        );
      }
    }
  };
  executeBatchCall();
});

export const createAudienceListAndLink: (
  journeyId: string,
  source: AudienceListSource,
  isDbCleanup?: boolean,
) => ThunkAction<Partial<AudienceListApiResponse>> =
  (journeyId, source, isDbCleanup = false) =>
  (dispatch: Dispatch, getState: GetState) => {
    const audienceList = getState().audienceList;
    const {name, entityType, inclusions, exclusions, all, ruleCreationType} =
      audienceList;
    //@ts-expect-error
    const rule = normalizeAudienceBlocks({inclusions, exclusions, all});
    //create
    const apiPayload = {
      filter: {
        name,
        rule,
        entityType,
        source,
      },
      resourceType: 'workflow',
      resourceId: journeyId,
      dbCleanup: isDbCleanup,
    } as const;
    return dispatch(reduxApi.post(`workflows/create-and-map`, apiPayload)).then(
      (payload) => {
        const listV2TrackingParams = getTrackingProps(payload.rule);
        AnalyticsService.track(ENGAGE.JOURNEY_LIST_ADDED, {
          ...apiPayload,
          workflow_id: journeyId,
          list_addition_method: 'new_list',
          rule_creation_method: ruleCreationType,
          list_id: payload.id,
          ...listV2TrackingParams,
        });
        return payload;
      },
    );
  };
